import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    TrialTypeBreadcrumbs,
    TrialTypeCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsTrialSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialTypeActions from "../../../actions/trialType";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { TrialLookupsLayout } from "../../index";

import "./TrialTypePage.scss";

interface TrialTypePageProps {
    form: Dtos.TrialType & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    trialType: Dtos.TrialType;
    lookups: Dtos.Lookup[];
    loadingTrialType: boolean;
    loadTrialTypeFailure: boolean;
    loadTrialTypeErrorCode: string;
    savingTrialType: boolean;
    saveTrialTypeSuccess: boolean;
    saveTrialTypeFailure: boolean;
    creatingTrialType: boolean;
    createTrialTypeSuccess: boolean;
    createTrialTypeFailure: boolean;
    validatingTrialType: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialType: TrialTypeActions.ITrialTypeClearActionCreator;
    loadTrialType: TrialTypeActions.ITrialTypeLoadByIdActionCreator;
    saveTrialType: TrialTypeActions.ITrialTypeSaveActionCreator;
    createTrialType: TrialTypeActions.ITrialTypeCreateActionCreator;
    validateTrialType: TrialTypeActions.ITrialTypeValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trialType";

const TrialTypePage: FC<TrialTypePageProps> = ({
    form,
    formState,
    formProperties,
    trialType,
    lookups,
    loadingTrialType,
    loadTrialTypeFailure,
    loadTrialTypeErrorCode,
    savingTrialType,
    saveTrialTypeSuccess,
    saveTrialTypeFailure,
    creatingTrialType,
    createTrialTypeSuccess,
    validatingTrialType,
    validationFailures,
    permissions,
    clearTrialType,
    loadTrialType,
    saveTrialType,
    createTrialType,
    validateTrialType,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialTypeId } = useParams<{ trialTypeId: string }>();
    const trialTypeIdNumber = parseInt(trialTypeId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.TrialType = {} as Dtos.TrialType;

        loadForm(reduxFormName, Object.assign({ ...trialType }, extraData));
    };


    // Load trial type
    useEffect(() => {
        if (trialTypeIdNumber && !trialType && !loadingTrialType) {
            setDrawerItems(SettingsTrialSubMenu(permissions));
            setBreadcrumbs(TrialTypeBreadcrumbs(trialTypeIdNumber));
            loadTrialType(trialTypeIdNumber);
        } else if (
            !trialTypeIdNumber &&
            !creatingTrialType &&
            !createTrialTypeSuccess
        ) {
            createTrialType();
            setBreadcrumbs(TrialTypeCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (trialType && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        trialTypeIdNumber,
        trialType,
        setDrawerItems,
        SettingsTrialSubMenu,
        permissions,
        setBreadcrumbs,
        TrialTypeBreadcrumbs,
        loadTrialType,
        createTrialType,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingTrialType,
        creatingTrialType,
        createTrialTypeSuccess,
        TrialTypeCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialTypeFailure) {
            navigate(`/error/${loadTrialTypeErrorCode}`);
        }
    }, [loadTrialTypeFailure, loadTrialTypeErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialTypeIdNumber && trialType?.value) {
            setBreadcrumbs(
                TrialTypeBreadcrumbs(trialTypeIdNumber, trialType?.value)
            );
        }
    }, [trialType, setBreadcrumbs, TrialTypeBreadcrumbs, trialTypeIdNumber]);

    // Reload trial type after saving
    useEffect(() => {
        if (!savingTrialType && saveTrialTypeSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial Type saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/trial/trialtypes";
                navigate(url);
            } else if (!trialTypeIdNumber) {
                let url: string = `/settings/trial/trialtypes/${trialType?.id}`;
                navigate(url);
                clearTrialType();
            }
        }
    }, [
        savingTrialType,
        saveTrialTypeSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrialType && saveTrialTypeFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrialType, saveTrialTypeFailure]);

    // Cleanup effect to clear the trial type on component unmount
    useEffect(() => {
        return () => {
            clearTrialType();
            resetForm(reduxFormName);
        };
    }, [clearTrialType, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/trial/trialtypes";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialType) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialType = {} as Dtos.TrialType;

            await saveTrialType(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialType = {} as Dtos.TrialType;

            if (form && hasBeenSubmitted && !validatingTrialType) {
                await validateTrialType(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.TrialTypesUpdate,
                Dtos.Permission.TrialTypesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialType ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trialType",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "trialType",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "trialType",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trialType",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingTrialType,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <TrialLookupsLayout
            loading={loadingTrialType || savingTrialType || creatingTrialType}
            permission={Dtos.Permission.TrialTypesView}
        >
            {renderContent}
        </TrialLookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialTypePageProps> => {
    let trialType: Dtos.TrialType | undefined = !(
        state.trialTypes.formData instanceof Array
    )
        ? state.trialTypes.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialType: trialType!,
        loadingTrialType:
            state.trialTypes.loadState &&
            state.trialTypes.loadState.status == RequestState.Pending,
        loadTrialTypeFailure:
            state.trialTypes.loadState &&
            state.trialTypes.loadState.status == RequestState.Failure,
        loadTrialTypeErrorCode: state.trialTypes.loadState.errorCode,
        savingTrialType:
            state.trialTypes.saveState &&
            state.trialTypes.saveState.status == RequestState.Pending,
        saveTrialTypeSuccess:
            state.trialTypes.saveState &&
            state.trialTypes.saveState.status == RequestState.Success,
        saveTrialTypeFailure:
            state.trialTypes.saveState &&
            state.trialTypes.saveState.status == RequestState.Failure,
        creatingTrialType:
            state.trialTypes.createState &&
            state.trialTypes.createState.status == RequestState.Pending,
        createTrialTypeSuccess:
            state.trialTypes.createState &&
            state.trialTypes.createState.status == RequestState.Success,
        createTrialTypeFailure:
            state.trialTypes.createState &&
            state.trialTypes.createState.status == RequestState.Failure,
        validatingTrialType:
            state.trialTypes.validateState &&
            state.trialTypes.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.trialType,
        formState: state.trialTypes.formState,
        formProperties: state.trialTypes.formProperties,
        validationFailures: trialType
            ? state.trialTypes.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialTypePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialType: bindActionCreators(
            TrialTypeActions.LoadTrialTypeById,
            dispatch
        ),
        clearTrialType: bindActionCreators(TrialTypeActions.Clear, dispatch),
        saveTrialType: bindActionCreators(
            TrialTypeActions.SaveTrialType,
            dispatch
        ),
        createTrialType: bindActionCreators(
            TrialTypeActions.CreateTrialType,
            dispatch
        ),
        validateTrialType: bindActionCreators(
            TrialTypeActions.ValidateTrialType,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialTypePage);
